import { readItems } from '@directus/sdk';
import { useQuery } from '@tanstack/react-query';

import type { Event } from '../../../../types/DirectusSchema';
import { directusClient } from '../../services/directusClient/directusClient.ts';
import { parseEvent } from '../lib/parseEvent.ts';
import { usePromoterItemQuery } from '../promoter/usePromoterItemQuery.ts';

type EventStatus = 'created' | 'published' | 'declined';

export type SortOption = 'start' | '-start' | 'name' | '-name' | 'date_created' | '-date_created';

/**
 * Published events pile up once they are over. "upcoming" keeps the dashboard to events that
 * still have to happen; "all" is the full list including past ones.
 */
export type Timeframe = 'all' | 'upcoming';

interface EventsQueryParams {
  status: EventStatus;
  page: number;
  limit: number;
  sort: SortOption;
  categoryId?: number;
  eventType?: 'online' | 'live' | 'secret';
  timeframe?: Timeframe;
  enabled?: boolean;
}

export const usePaginatedEventsQuery = ({
  status,
  page,
  limit,
  sort,
  categoryId,
  eventType,
  timeframe,
  enabled,
}: EventsQueryParams) => {
  const { data } = usePromoterItemQuery();

  return useQuery({
    queryKey: [
      'events',
      'paginated',
      { status, page, limit, sort, categoryId, eventType, timeframe, promoterId: data?.id },
    ],
    queryFn: async () => {
      const filter: Record<string, unknown> = {
        promoter: { _eq: data?.id },
        status: { _eq: status },
      };

      if (categoryId) {
        filter.category = { _eq: categoryId };
      }

      if (eventType) {
        filter.type = { _eq: eventType };
      }

      // An event counts as upcoming until it has finished, so compare on end, not start.
      if (timeframe === 'upcoming') {
        filter.end = { _gte: new Date().toISOString() };
      }

      const result = await directusClient.request(
        readItems('events', {
          filter,
          fields: [
            '*',
            {
              venue: ['id', 'commercial_name'],
              category: ['id', 'title'],
              target_audiences: ['id', { target_audiences_id: ['*'] }],
            },
          ],
          sort: [sort],
          limit,
          offset: (page - 1) * limit,
        }),
      );

      return result?.map((e) => parseEvent(e as unknown as Event));
    },
    enabled: enabled !== false && !!data?.id,
  });
};
